/** @jsxImportSource @emotion/react */
import React, { forwardRef } from "react";
import { Link, LinkProps } from "react-router-dom";
import { StyledText } from "../assets/typography/typography";
import { colors } from "./colors";
import { css } from "@emotion/react";
import { effects } from "./effects";
import { Dropdown, DropdownProps } from "antd";
import { DropdownMenuWrapper } from "../module/common/DropdownMenuWrapper";

interface ButtonProps extends React.HTMLProps<HTMLButtonElement> {
  icon?: React.ReactNode;
  type?: "submit" | "button" | "reset" | undefined;
  buttonSize?: "large" | "small";
  innerRef?: any;
}

type LinkButtonProps = {
  width?: string;
  height?: string;
  type?: "dark" | "light";
  icon?: React.ReactNode;
} & React.ClassAttributes<HTMLAnchorElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement>;

type InternalLinkButtonProps = {
  width?: string;
  height?: string;
  type?: "dark" | "light";
  icon?: React.ReactNode;
} & LinkProps &
  React.RefAttributes<HTMLAnchorElement>;

export const baseButtonStyle = css`
  display: flex;
  flex-direction: row;
  border-style: none;
  justify-content: center;
  text-align: center;
  display: inline-flex;
  align-items: center;
  border-radius: 6px;
  padding: 8px 16px;
  gap: 8px;
`;

export const ButtonPrimary = ({
  color,
  icon,
  width,
  height,
  children,
  disabled,
  buttonSize = "large",
  type,
  ...rest
}: ButtonProps) => {
  const buttonStyle = css`
    ${baseButtonStyle}
    ${!disabled &&
    `
    cursor: pointer;
    `}
    box-shadow: ${effects.buttonPrimary};
    background: ${disabled ? colors.ctaInactive : colors.buttonPrimaryWhite};
    color: ${disabled ? colors.textSecondary : colors.textPrimary};
    width: ${width || "max-content"};
    height: ${height || "fit-content"};
    min-height: ${height || "fit-content"};
    min-width: ${width || "max-content"};
    &:hover {
      ${!disabled &&
      `
      background: ${colors.whiteHover};
      `}
    }
  `;

  const textClassname =
    buttonSize === "large" ? "bodyTextSemibold" : "bodySubtextSemibold";

  return (
    <button
      css={buttonStyle}
      disabled={disabled}
      type={type || "button"}
      {...rest}
    >
      {icon}
      <StyledText className={`${textClassname} mb-0`}>{children}</StyledText>
    </button>
  );
};

export const ButtonPrimaryForwardedRef = forwardRef(
  (
    {
      disabled,
      height,
      width,
      type,
      icon,
      children,
      buttonSize,
      ...rest
    }: ButtonProps,
    ref
  ) => {
    const buttonStyle = css`
      ${baseButtonStyle}
      ${!disabled &&
      `
    cursor: pointer;
    `}
    box-shadow: ${effects.buttonPrimary};
      background: ${disabled ? colors.ctaInactive : colors.buttonPrimaryWhite};
      color: ${disabled ? colors.textSecondary : colors.textPrimary};
      width: ${width || "max-content"};
      height: ${height || "fit-content"};
      min-height: ${height || "fit-content"};
      min-width: ${width || "max-content"};
      &:hover {
        ${!disabled &&
        `
      background: ${colors.whiteHover};
      `}
      }
    `;

    const textClassname =
      buttonSize === "large" ? "bodyTextSemibold" : "bodySubtextSemibold";

    return (
      <button
        css={buttonStyle}
        disabled={disabled}
        type={type || "button"}
        ref={ref as any}
        {...rest}
      >
        {icon}
        <StyledText className={`${textClassname} mb-0`}>{children}</StyledText>
      </button>
    );
  }
);

export const ButtonExternalLink = ({
  type = "light",
  width,
  height,
  icon,
  children,
  ...rest
}: LinkButtonProps) => {
  const linkStyle = css`
    ${baseButtonStyle}
    cursor: pointer;
    box-shadow: ${type === "light"
      ? effects.buttonPrimary
      : effects.buttonAction};
    background: ${type === "light"
      ? colors.buttonPrimaryWhite
      : colors.buttonAction};
    color: ${type === "light" ? colors.textPrimary : colors.white};
    width: ${width || "max-content"};
    text-decoration: none !important;
    height: ${height || "fit-content"};
    min-height: ${height || "fit-content"};
    min-width: ${width || "max-content"};
    &:hover {
      background: ${type === "light"
        ? colors.whiteHover
        : colors.actionButtonHover};
      color: ${type === "light" ? colors.textPrimary : colors.white};
    }
  `;
  return (
    <a css={linkStyle} rel="noreferrer" target="_blank" {...rest}>
      {icon && (
        <StyledText className="bodyTextSemibold mb-0">{icon}</StyledText>
      )}
      <StyledText className="bodyTextSemibold mb-0">{children}</StyledText>
    </a>
  );
};

export const ButtonAction = ({
  icon,
  width,
  className,
  children,
  style,
  height,
  disabled,
  buttonSize = "large",
  backgroundColor,
  hoverColor,
  textColor,
  innerRef,
  ...rest
}: ButtonProps & {
  backgroundColor?: string;
  hoverColor?: string;
  textColor?: string;
}) => {
  const buttonStyle = css`
    ${baseButtonStyle}
    ${!disabled &&
    `
    cursor: pointer;
    `}
    box-shadow: ${!disabled
      ? !!backgroundColor
        ? backgroundColor
        : effects.buttonAction
      : effects.buttonPrimary};
    background: ${disabled
      ? colors.ctaInactive
      : !!backgroundColor
      ? backgroundColor
      : colors.buttonAction};
    color: ${disabled
      ? colors.textSecondary
      : !!textColor
      ? textColor
      : colors.white};
    width: ${width || "max-content"};
    height: ${height || "fit-content"};
    min-height: ${height || "fit-content"};
    min-width: ${width || "max-content"};
    &:hover {
      ${!disabled &&
      `
      background: ${!!hoverColor ? hoverColor : colors.actionButtonHover};
      `}
    }
  `;
  const textClassname =
    buttonSize === "large" ? "bodyTextSemibold" : "bodySubtextSemibold";

  return (
    <button css={buttonStyle} disabled={disabled} ref={innerRef} {...rest}>
      {icon}
      <StyledText className={`${textClassname} mb-0`}>{children}</StyledText>
    </button>
  );
};

export const ButtonActionForwardedRef = forwardRef(
  (props: ButtonProps, ref) => {
    return <ButtonAction {...props} innerRef={ref} />;
  }
);

export const DeleteButton = ({
  icon,
  width,
  children,
  disabled,
  height,
  buttonSize = "large",
  ...rest
}: ButtonProps) => {
  const buttonStyle = css`
    ${baseButtonStyle}
    ${!disabled &&
    `
    cursor: pointer;
    `}
    box-shadow: ${!disabled ? effects.deleteButton : effects.buttonPrimary};
    background: ${disabled ? colors.ctaInactive : colors.buttonDelete};
    color: ${disabled ? colors.textSecondary : colors.white};
    width: ${width || "max-content"};
    height: ${height || "fit-content"};
    min-height: ${height || "fit-content"};
    min-width: ${width || "max-content"};
    &:hover {
      ${!disabled &&
      `
      background: ${colors.errorMedium};
      `}
    }
  `;
  const textClassname =
    buttonSize === "large" ? "bodyTextSemibold" : "bodySubtextSemibold";

  return (
    <button css={buttonStyle} disabled={disabled} {...rest}>
      {icon}
      <StyledText className={`${textClassname} mb-0`}>{children}</StyledText>
    </button>
  );
};

export const ButtonDisabledPlaceholder = ({
  icon,
  width,
  children,
  height,
  ...rest
}: ButtonProps) => {
  const buttonStyle = css`
    ${baseButtonStyle}
    box-shadow: ${effects.buttonPrimary};
    background: ${colors.ctaInactive};
    color: ${colors.textSecondary};
    width: ${width || "max-content"};
    height: ${height || "fit-content"};
    min-height: ${height || "fit-content"};
    min-width: ${width || "max-content"};
  `;

  return (
    <button css={buttonStyle} {...rest}>
      {icon}
      <StyledText className="bodyTextSemibold mb-0">{children}</StyledText>
    </button>
  );
};

export const ButtonInternalLink = ({
  icon,
  width,
  children,
  type = "light",
  height,
  style,
  ...rest
}: InternalLinkButtonProps) => {
  const linkStyle = css`
    ${baseButtonStyle}
    cursor: pointer;
    box-shadow: ${type === "light"
      ? effects.buttonPrimary
      : effects.buttonAction};
    background: ${type === "light"
      ? colors.buttonPrimaryWhite
      : colors.buttonAction};
    text-decoration: none !important;
    color: ${type === "light" ? colors.textPrimary : colors.white};
    width: ${width || "max-content"};
    height: ${height || "fit-content"};
    min-height: ${height || "fit-content"};
    min-width: ${width || "max-content"};
    &:hover {
      background: ${type === "light"
        ? colors.whiteHover
        : colors.actionButtonHover};
      color: ${type === "light" ? colors.textPrimary : colors.white};
    }
  `;

  return (
    <Link css={linkStyle} {...rest}>
      {icon}
      <StyledText className="bodyTextSemibold mb-0">{children}</StyledText>
    </Link>
  );
};

export const ButtonIcon = ({
  color,
  icon,
  width,
  height,
  children,
  disabled,
  buttonSize = "large",
  ...rest
}: ButtonProps) => {
  const buttonStyle = css`
    display: flex;
    flex-direction: row;
    border-style: none;
    justify-content: center;
    text-align: center;
    align-items: center;
    width: 24px;
    height: 24px;
    min-height: 24px;
    min-width: 24px;
    ${!disabled &&
    `
    cursor: pointer;
    `}
    background: ${colors.transparent};
    color: ${colors.textSecondary};
    &:hover {
      ${!disabled &&
      `
      color: ${colors.textPrimary};
      `}
    }
  `;

  const textClassname =
    buttonSize === "large" ? "bodyTextSemibold" : "bodySubtextSemibold";

  return (
    <button css={buttonStyle} disabled={disabled} {...rest}>
      <StyledText className={`${textClassname} mb-0`}>{children}</StyledText>
    </button>
  );
};

type DropdownButtonProps = {
  buttonSize: "large" | "small";
  width?: string;
  height?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
} & DropdownProps;

export const DropdownButton = ({
  children,
  disabled,
  width,
  height,
  buttonSize = "large",
  rightIcon,
  leftIcon,
  ...rest
}: DropdownButtonProps) => {
  const buttonStyle = css`
    ${baseButtonStyle}
    ${!disabled &&
    `
    cursor: pointer;
    `}
    box-shadow: ${effects.buttonPrimary};
    background: ${disabled ? colors.ctaInactive : colors.buttonPrimaryWhite};
    color: ${disabled ? colors.textSecondary : colors.textPrimary};
    width: ${width || "max-content"};
    height: ${height || "fit-content"};
    min-height: ${height || "fit-content"};
    min-width: ${width || "max-content"};
    &:hover {
      ${!disabled &&
      `
      background: ${colors.whiteHover};
      `}
    }
  `;

  const dropdownInnerWrapper = css`
    display: flex;
    flex-direction: column;
    padding: 8px;
  `;

  const textClassname =
    buttonSize === "large" ? "bodyTextSemibold" : "bodySubtextSemibold";

  return (
    <Dropdown
      placement="bottomRight"
      dropdownRender={(menu) => (
        <DropdownMenuWrapper>
          <div css={dropdownInnerWrapper}>
            {React.cloneElement(menu as React.ReactElement, {
              style: {
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                boxShadow: "none",
                padding: "0px",
                backgroundColor: "transparent",
              },
            })}
          </div>
        </DropdownMenuWrapper>
      )}
      {...rest}
    >
      <div css={buttonStyle}>
        {leftIcon && leftIcon}
        <StyledText className={`${textClassname} mb-0`}>{children}</StyledText>
        {rightIcon && rightIcon}
      </div>
    </Dropdown>
  );
};
